import { useQueryClient } from '@tanstack/react-query';
import { addHours, isAfter, subMonths } from 'date-fns';
import { useCallback } from 'react';

import { isSigningOut } from '@/services/auth';
import { getInquiriesV2 } from '@/services/getInquiriesV2';
import {
  applyCalloutAction,
  CALLOUT_KEYS,
  getUserCalloutsQueryKey,
  getUserCalloutsQueryOptions,
  selectCallout,
  type UserCallout,
} from '@/services/userCallouts';
import { Sentry } from '@/util/sentry';

const NEW_USER_WINDOW_MONTHS = 1;

const REMIND_LATER_DELAY_HOURS = 24;

/**
 * Runs a callout request, reporting a failure instead of throwing.
 *
 * Deliberately not `safeStorageOp`, despite the identical shape: that helper
 * is named for storage and these are network calls, and a reader who sees it
 * here would reasonably conclude this hook still writes to the device.
 */
const reportingFailure = async <T>(op: () => Promise<T>): Promise<T | null> => {
  try {
    return await op();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { callout: CALLOUT_KEYS.ONBOARDING_TUTORIAL_INTRO },
    });

    return null;
  }
};

/**
 * Gates the onboarding tutorial: show it once to a genuinely new user, let
 * them defer it for a day, and never show it again once seen.
 *
 * State is a server-backed callout rather than device storage. The previous
 * implementation took a `userId` and passed it to `getPlatformStorage`, which
 * reads as per-user and is not — that argument only selects the storage
 * backend, so the keys were shared by every account on a device. A second
 * person signing in on the same browser inherited the first person's
 * dismissal and never saw the tutorial, and the same person on a second
 * device saw it again.
 *
 * The callout read fails closed: if it errors, `shouldShow` returns false.
 * Re-showing a tutorial someone already dismissed is worse than missing one
 * for a user who would have watched it.
 *
 * The inquiry check does not, and deliberately so. A failure there leaves the
 * hook unable to tell whether the user has inquiries, and it proceeds as if
 * they have none. Showing the tutorial to an established user is a smaller
 * cost than withholding it from a new one on a transient API blip. Both
 * directions are mild, so this is the pre-existing behavior left alone
 * rather than a considered improvement.
 */
export const useOnboardingTutorialModal = (userId?: string) => {
  const queryClient = useQueryClient();

  // Keyed on `userId`, not on the query key itself: the key helper returns a
  // fresh array every call, so depending on it would rebuild this callback
  // every render and defeat the memoization for everything downstream.
  const invalidateCallouts = useCallback(
    () => queryClient.invalidateQueries({ queryKey: getUserCalloutsQueryKey(userId) }),
    [queryClient, userId]
  );

  const markShown = useCallback(async () => {
    if (!userId) {
      return;
    }

    // `resolve`, not `dismiss`: the tutorial is a one-time introduction with
    // no de-escalated second stage to fall back to.
    await reportingFailure(() =>
      applyCalloutAction(CALLOUT_KEYS.ONBOARDING_TUTORIAL_INTRO, 'resolve')
    );

    await invalidateCallouts();
  }, [userId, invalidateCallouts]);

  const markShowLater = useCallback(async () => {
    if (!userId) {
      return;
    }

    await reportingFailure(() =>
      applyCalloutAction(
        CALLOUT_KEYS.ONBOARDING_TUTORIAL_INTRO,
        'snooze',
        addHours(new Date(), REMIND_LATER_DELAY_HOURS).toISOString()
      )
    );

    await invalidateCallouts();
  }, [userId, invalidateCallouts]);

  const shouldShow = useCallback(
    async (
      userData?: { created_at?: string | null } | null,
      needsNpiVerification?: boolean
    ): Promise<boolean> => {
      if (!userId || needsNpiVerification || isSigningOut()) {
        return false;
      }

      const callouts = await reportingFailure<UserCallout[]>(() =>
        queryClient.fetchQuery(getUserCalloutsQueryOptions(userId))
      );

      // Null means the fetch failed. Treat that as suppressing rather than
      // showing, so an outage cannot re-surface a resolved tutorial.
      if (!callouts) {
        return false;
      }

      const callout = selectCallout(callouts, CALLOUT_KEYS.ONBOARDING_TUTORIAL_INTRO);

      if (callout?.resolvedAt) {
        return false;
      }

      if (callout?.deferredUntil && isAfter(new Date(callout.deferredUntil), new Date())) {
        return false;
      }

      const inquiries = await reportingFailure(() => getInquiriesV2());

      if (inquiries?.inquiries?.length) {
        return false;
      }

      if (!userData?.created_at) {
        return true;
      }

      return isAfter(new Date(userData.created_at), subMonths(new Date(), NEW_USER_WINDOW_MONTHS));
    },
    [userId, queryClient]
  );

  return { markShown, markShowLater, shouldShow };
};
